import { useState, type Dispatch, type SetStateAction } from 'react';
import { useOffline } from '../contexts/OfflineContext';
import { formatCurrency } from '../core/format/currency';
import { normalizeRouteStop, type RouteStop } from '../core/models/RouteStop';
import type { IDataAdapter } from '../adapters/data/IDataAdapter';
import type { AppNotification } from '../core/models/index';
import { useBankerRouteStopActions } from './useBankerRouteStopActions';

type T = Record<string, string>;
type Push = (fields: Partial<AppNotification>, source?: string) => AppNotification | null;

type Opts = {
  t: T;
  push: Push;
  adapter: IDataAdapter;
  userId: string;
  houseId: string;
  departureOk: boolean;
  setLocalStops: Dispatch<SetStateAction<Record<string, RouteStop>>>;
  setRemovedIds: Dispatch<SetStateAction<Record<string, true>>>;
};

/** Edit form state + stop actions for the banker route screen. */
export function useBankerRouteStops(opts: Opts) {
  const { t, push, adapter, setLocalStops } = opts;
  const { online, enqueue } = useOffline();
  const [editId, setEditId] = useState<string | null>(null);
  const [addr, setAddr] = useState('');
  const [expected, setExpected] = useState('');
  const [priority, setPriority] = useState<'normal' | 'high'>('normal');
  const [skipReason, setSkipReason] = useState('');
  const [saveBusy, setSaveBusy] = useState(false);

  const resetEditForm = () => {
    setEditId(null);
    setAddr('');
    setExpected('');
    setPriority('normal');
    setSkipReason('');
    setSaveBusy(false);
  };

  const actions = useBankerRouteStopActions({
    ...opts,
    skipReason,
    onAfterMutation: resetEditForm,
  });

  const cancelEdit = () => {
    resetEditForm();
    actions.setSkipBusy(false);
    actions.setDeleteBusy(false);
  };

  const startEdit = (stop: RouteStop) => {
    setEditId(stop.id);
    setAddr(stop.addr ?? '');
    setExpected(String(stop.expectedAmount ?? ''));
    setPriority(stop.priority === 'high' ? 'high' : 'normal');
    setSkipReason('');
  };

  const saveEdit = async (stop: RouteStop) => {
    if (saveBusy) return;
    const expectedAmount = Number(expected);
    const patch = {
      addr: addr.trim(),
      expectedAmount: Number.isFinite(expectedAmount) ? expectedAmount : 0,
      priority,
    };
    const optimistic = normalizeRouteStop({ ...stop, ...patch });
    setLocalStops((prev) => ({ ...prev, [stop.id]: optimistic }));
    setSaveBusy(true);
    const payload = { stopId: stop.id, patch };
    try {
      if (!online || !adapter.updateRouteStop) {
        enqueue({ type: 'updateRouteStop', payload });
      } else {
        await adapter.updateRouteStop(payload);
      }
      push({
        cat: 'routes',
        sev: 'success',
        title: t.saved ?? t.saveStop,
        body: `${stop.name} · ${formatCurrency(optimistic.expectedAmount)}`,
      });
      cancelEdit();
    } catch {
      try {
        enqueue({ type: 'updateRouteStop', payload });
        push({ cat: 'routes', sev: 'warn', title: t.saved ?? t.saveStop, body: stop.name });
        cancelEdit();
      } catch {
        setLocalStops((prev) => {
          const next = { ...prev };
          delete next[stop.id];
          return next;
        });
        push({
          cat: 'routes',
          sev: 'danger',
          title: t.saveStopFailed ?? t.err_generic,
          body: stop.name,
        });
        setSaveBusy(false);
      }
    }
  };

  return {
    editId,
    addr,
    setAddr,
    expected,
    setExpected,
    priority,
    setPriority,
    saveBusy,
    startEdit,
    cancelEdit,
    saveEdit,
    secureBusyId: actions.secureBusyId,
    reviewBusyId: actions.reviewBusyId,
    skipBusy: actions.skipBusy,
    deleteBusy: actions.deleteBusy,
    secureCollect: actions.secureCollect,
    markDiscrepancyReviewed: actions.markDiscrepancyReviewed,
    skip: actions.skip,
    reviewSkip: actions.reviewSkip,
    removeAdhoc: actions.removeAdhoc,
  };
}
